import { config } from '../config';
import { rumRecordError, rumRecordEvent } from './rum';

export type ReportContext = {
  source: 'queryCache' | 'mutationCache' | 'errorBoundary';
  queryKey?: unknown;
  route?: string;
};

/**
 * Keys whose values MUST be redacted — credentials/secrets only.
 *
 * Narrowed at S17a per ADR-0027: the accepted-PII decision deliberately
 * KEEPS identity (`email`, `name`, `tenantSlug`) in RUM telemetry so a
 * client error is attributable to a specific tenant + user during
 * triage. Credentials (`access_token`, `id_token`, `refresh_token`,
 * `authorization`, OAuth `code`/`state`) stay scrubbed unconditionally.
 *
 * SR1/SR6: identity in (an accepted, bounded risk — the RUM store is
 * AWS-internal, IAM-scoped, retention-bounded); secrets NEVER out.
 */
const SENSITIVE_KEYS = new Set([
  'authorization',
  'access_token',
  'id_token',
  'refresh_token',
  'code',
  'state',
]);

// Matches an entire string that is a compact JWT (three base64url segments).
const JWT_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
// Matches an entire string that is a Bearer header value.
const BEARER_RE = /^Bearer\s+\S+/i;

function redactString(value: string): string {
  // Entire string is a JWT or a bearer header value — replace the whole thing.
  if (JWT_RE.test(value.trim()) || BEARER_RE.test(value)) return '[redacted]';
  // Inline replacements for mixed strings (e.g. error messages containing a token).
  let out = value.replace(/\bBearer\s+\S+/gi, '[redacted]');
  // Strip OAuth code/state query params from URLs (avoids URL-encoding issues from the URL API).
  out = out.replace(/([?&])(code|state)=[^&\s#]*/gi, '$1$2=[redacted]');
  return out;
}

export function scrub<T>(value: T): T {
  if (typeof value === 'string') return redactString(value) as T;
  if (Array.isArray(value)) return value.map(scrub) as T;
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? '[redacted]' : scrub(v);
    }
    return out as T;
  }
  return value;
}

function normalise(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: redactString(error.message),
      stack: error.stack,
      ...(error.cause !== undefined ? { cause: normalise(error.cause) } : {}),
    };
  }
  return { name: 'UnknownError', message: String(scrub(error)) };
}

function errorNameOf(error: unknown): string {
  if (error instanceof Error) return error.name || 'Error';
  return 'UnknownError';
}

export function reportError(error: unknown, context?: ReportContext): void {
  const scrubbedError = normalise(error);
  const scrubbedContext = context ? scrub(context) : undefined;

  if (!config.isDeployedEnv) {
    console.error('[reportError]', scrubbedError, scrubbedContext);
    return;
  }

  // ── Deployed env: RUM transport ───────────────────────────────────────
  // 1. Always forward the caught error via recordError (drives the
  //    built-in AWS/RUM JsErrorCount metric / dashboard / alarm).
  rumRecordError(error, { error: scrubbedError, context: scrubbedContext });

  // 2. For the targeted create-flow path emit the custom event that the
  //    RumAppMonitor's metric definition matches (drives the per-tenant
  //    `CreateFlowErrorCount` alarm). The mutation+errorBoundary paths
  //    are the create-flow's user-visible failure surface.
  if (context?.source === 'mutationCache' || context?.source === 'errorBoundary') {
    const route =
      typeof scrubbedContext?.route === 'string'
        ? scrubbedContext.route
        : typeof window !== 'undefined' && window.location
          ? window.location.pathname
          : 'unknown';
    // tenant + username are inlined by rumRecordEvent from getRumIdentity()
    // — keeping that in one place means we never accidentally bypass it.
    rumRecordEvent('com.agentisai.app_error', {
      route,
      source: context.source,
      errorName: errorNameOf(error),
    });
  }
}
